import { EstateLevel } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { EstateHotelDetails } from "@1games-ru/region-shared/dist/types/estate/estate";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { BankCardIcon, MapPin3Icon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import FeeSelectDaysSlider from "@/shared/widgets/FeeSelectDaysSlider";
import { getFormattedDate } from "@/domains/hotel/helpers/getFormattedDate";
import useGetServerDate from "@/domains/hotel/hooks/useGetServerDate";
import HotelSelectCard from "@/domains/hotel/components/HotelSelectCard";

type Props = {
  hotelId: number | null;
  data: EstateHotelDetails;
  isLoading: boolean;
  onClose: () => void;
  isPaymentLoading: boolean;
  onPay: (id: number, level: EstateLevel, paidDays: number, paymentType: PaymentType) => void;
};

export default function HotelSelectContainer({
  hotelId,
  data,
  isLoading,
  onClose,
  isPaymentLoading,
  onPay,
}: Props) {
  const { t } = useTranslation("hotel");

  const [activeCard, setActiveCard] = useState<0 | 1 | null>(null);
  const [isInactiveFirst, setIsInactiveFirst] = useState(false);
  const [isInactiveSecond, setIsInactiveSecond] = useState(false);
  const [selectedDays, setSelectedDays] = useState(1);

  const data1 = data?.rooms[0];
  const data2 = data?.rooms[1];

  const viewControls = activeCard === null || data?.rooms[activeCard]?.freeCount > 0;

  const serverDate = useGetServerDate();
  const nearestDays = useMemo(() => {
    if (typeof activeCard !== "number") return null;

    const days = data?.rooms[activeCard]?.nearestDays;
    if (days === null) return "Не известно";

    return getFormattedDate(days, serverDate);
  }, [activeCard, data?.rooms, serverDate]);

  const totalPrice = useMemo(() => {
    if (activeCard === null || !data?.rooms?.[activeCard]?.price || !selectedDays) return 0;
    return data.rooms[activeCard].price * selectedDays;
  }, [activeCard, data?.rooms, selectedDays]);

  const min = 0;
  const max = data?.maxFeeDays || 0;
  const step = 1;
  const values = useMemo(() => Array.from({ length: max + 1 }, (_, i) => i), [max]);

  const handleDaysChange = (newValue: number) => {
    setSelectedDays(newValue);
  };

  const onHandleFirstCardClick = useCallback(() => {
    if (data1?.totalCount) {
      setActiveCard(0);
      setIsInactiveSecond(true);
    }
  }, [data1?.totalCount]);

  const onHandleSecondCardClick = useCallback(() => {
    if (data2?.totalCount) {
      setActiveCard(1);
      setIsInactiveFirst(true);
    }
  }, [data2?.totalCount]);

  const onCardHover = useCallback(() => {
    setIsInactiveFirst(false);
    setIsInactiveSecond(false);
  }, []);

  const onCashPay = useCallback(() => {
    if (hotelId && data?.rooms[activeCard]?.level && selectedDays) {
      onPay(hotelId, data?.rooms[activeCard]?.level, selectedDays, PaymentType.Cash);
    }
  }, [activeCard, data?.rooms, hotelId, onPay, selectedDays]);

  const onCardPay = useCallback(() => {
    if (hotelId && data?.rooms[activeCard]?.level && selectedDays) {
      onPay(hotelId, data?.rooms[activeCard]?.level, selectedDays, PaymentType.Card);
    }
  }, [activeCard, data?.rooms, hotelId, onPay, selectedDays]);

  const title = !isLoading ? (
    <div className="flex gap-3 items-center">
      <MapPin3Icon className="w-5.5" />
      <p className="text-20 font-bold leading-none">{data?.name}</p>
    </div>
  ) : null;

  const footer = viewControls ? (
    <div className="flex gap-2">
      <Button
        className="grow gap-2.5 h-11"
        onClick={onCashPay}
        loading={isPaymentLoading}
        disabled={isPaymentLoading || !selectedDays || typeof activeCard !== "number"}
      >
        <BankCardIcon className="w-4.5" />
        {t("hotel.paymentMethod.cash")}
      </Button>
      <Button
        className="grow gap-2.5 h-11"
        onClick={onCardPay}
        loading={isPaymentLoading}
        disabled={isPaymentLoading || !selectedDays || typeof activeCard !== "number"}
      >
        <BankCardIcon className="w-4.5" />
        {t("hotel.paymentMethod.card")}
      </Button>
    </div>
  ) : null;

  return (
    <Modal
      open
      title={title}
      footer={footer}
      onClose={onClose}
      className="w-[38.375rem] h-[52.3125rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      style={{ fontFamily: "TT Norms Pro" }}
      disableAnimation
    >
      {isLoading ? (
        <div className="absolute z-10 top-1/2 left-1/2 flex items-center justify-center p-4 -translate-x-1/2 -translate-y-1/2">
          <div className="mz-loader" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 mb-5">
            <HotelSelectCard
              isCheck={activeCard === 0}
              src={data1?.images[0]}
              uuid={data1?.interiorUuid}
              level={data1?.level}
              price={data1?.price}
              isInactive={isInactiveFirst}
              count={data1?.freeCount}
              onClick={onHandleFirstCardClick}
              onHover={onCardHover}
              list={data1?.features}
            />
            <HotelSelectCard
              isCheck={activeCard === 1}
              src={data2?.images[0]}
              uuid={data2?.interiorUuid}
              level={data2?.level}
              price={data2?.price}
              isInactive={isInactiveSecond}
              count={data2?.freeCount}
              onClick={onHandleSecondCardClick}
              onHover={onCardHover}
              list={data2?.features}
            />
          </div>
          {viewControls ? (
            <>
              <p className="flex justify-between mb-5 px-1 text-16 font-bold text-white leading-none">
                {t("hotel.daysForStay")}: <span className="text-accent-solid">{selectedDays}</span>
              </p>
              <FeeSelectDaysSlider
                value={selectedDays}
                values={values}
                min={min}
                max={max}
                step={step}
                displayStep={5}
                onChange={handleDaysChange}
                className="px-1.5"
                classNameSlider="[&_.rt-SliderTrack]:h-1 [&_.rt-SliderTrack]:bg-[#191919] [&_.rt-SliderTrack]:rounded-4 [&_.rt-SliderThumb]:w-1.5 [&_.rt-SliderThumb]:h-1.5"
                classNameLabel="mt-1.5"
                highlightActive
              />
              <div className="flex justify-between items-end mt-5 px-1">
                <p className="flex flex-col text-15 font-medium leading-none">
                  <span className="mb-0.5 text-20 font-bold text-accent-solid">
                    {formatPrice(totalPrice)} ₽
                  </span>
                  {t("hotel.totalCost")}
                </p>
                <p className="text-15 font-medium text-white/50 leading-none">
                  {t("hotel.chooseRoomType")}
                </p>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-end mt-5 px-1">
              <p className="flex flex-col text-15 font-medium leading-none">
                <span className="mb-1 text-18 font-bold text-accent-solid">
                  {t("hotel.allOccupied")}
                </span>
                {t("hotel.nextAvailableDate")}
              </p>
              <p className="text-15 font-medium text-white/50 leading-none">{nearestDays}</p>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
